import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useConfirm } from "@/hooks/useConfirm";
import { ProgramFormModal, type ProgramRow } from "@/components/dashboard/program-form-modal";

function ProgramListRow({
  program,
  onEdit,
  onArchiveToggle,
  onDelete,
}: {
  program: ProgramRow;
  onEdit: () => void;
  onArchiveToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      className={cx(
        cf.dashSurface,
        css({
          p: "4",
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { base: "4", sm: "3" },
          opacity: program.archived ? 0.5 : 1,
        }),
      )}
    >
      <div className={css({ minW: 0 })}>
        <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>
          {program.name}
          {program.archived && (
            <span className={css({ ml: "2", fontSize: "xs", color: "#94a3b8" })}>(archivado)</span>
          )}
        </p>
        {program.description ? (
          <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>{program.description}</p>
        ) : null}
        <p className={css({ fontSize: "sm", color: "#334155", mt: "1" })}>
          {program.durationMonths} {program.durationMonths === 1 ? "mes" : "meses"} de vigencia ·{" "}
          {program.sessions.length} {program.sessions.length === 1 ? "tipo de sesión" : "tipos de sesión"}
          {program.activeAssignmentCount > 0
            ? ` · ${program.activeAssignmentCount} ${program.activeAssignmentCount === 1 ? "paciente activo" : "pacientes activos"}`
            : ""}
        </p>
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1.5" })}>
          {program.sessions.map((s) => `${s.serviceName} × ${s.quantity}`).join(" · ")}
        </p>
      </div>
      <div
        className={css({
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          gap: { base: "2.5", sm: "2" },
          alignItems: { base: "stretch", sm: "center" },
          flexShrink: 0,
        })}
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={onEdit}
          className={css({ flex: { base: "1", sm: "initial" }, h: { base: "11", sm: "auto" } })}
        >
          Editar
        </Button>
        <button
          type="button"
          onClick={onArchiveToggle}
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: { base: "1", sm: "initial" },
            h: { base: "11", sm: "auto" },
            fontSize: "xs",
            px: "3",
            py: "1.5",
            rounded: "md",
            borderWidth: "1px",
            borderColor: program.archived ? "#86efac" : "#e2e8f0",
            color: program.archived ? "#16a34a" : "#334155",
            bg: "white",
            cursor: "pointer",
            whiteSpace: "nowrap",
            _hover: { bg: program.archived ? "#f0fdf4" : "#f8fafc" },
          })}
        >
          {program.archived ? "Reactivar" : "Archivar"}
        </button>
        {program.activeAssignmentCount === 0 ? (
          <button
            type="button"
            onClick={onDelete}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: { base: "1", sm: "initial" },
              h: { base: "11", sm: "auto" },
              fontSize: "xs",
              px: "3",
              py: "1.5",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "#fca5a5",
              color: "#dc2626",
              bg: "white",
              cursor: "pointer",
              whiteSpace: "nowrap",
              _hover: { bg: "#fef2f2" },
            })}
          >
            Eliminar
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function DashboardPrograms() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [programs, setPrograms] = useState<ProgramRow[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProgramRow | null>(null);

  const reload = useCallback(async () => {
    const r = await fetchWithSessionSync(
      "/api/dashboard/programs?includeArchived=true",
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) return;
    setPrograms((await r.json()) as ProgramRow[]);
  }, [refresh]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(p: ProgramRow) {
    setEditing(p);
    setModalOpen(true);
  }

  async function toggleArchive(p: ProgramRow) {
    await fetchWithSessionSync(
      `/api/dashboard/programs/${p.id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: !p.archived }),
      },
      refresh,
    );
    await reload();
  }

  async function deleteProgram(p: ProgramRow) {
    if (
      !(await confirm({
        message: `¿Eliminar el programa "${p.name}"? Esta acción no se puede deshacer.`,
        tone: "danger",
        confirmLabel: "Eliminar",
      }))
    )
      return;
    const r = await fetchWithSessionSync(
      `/api/dashboard/programs/${p.id}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      window.alert(j.error ?? "No se pudo eliminar el programa.");
      return;
    }
    await reload();
  }

  if (!programs) {
    return (
      <DashboardPageShell eyebrow="Catálogo" title="Programas">
        <LoadingFrame variant="light" minHeight={280} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Catálogo"
      title="Programas de tratamiento"
      description="Agrupá varias sesiones de servicios en un plan que tus pacientes consumen con el tiempo. Asignalos desde la ficha del paciente."
    >
      <div className={css({ display: "flex", justifyContent: "flex-end", mb: "5" })}>
        <Button variant="primary" onClick={openCreate} className={css({ w: { base: "full", sm: "auto" } })}>
          + Nuevo programa
        </Button>
      </div>

      {programs.length === 0 ? (
        <DashboardEmpty
          title="Aún no cargaste programas"
          hint="Usá el botón de arriba para crear el primero: elegí duración y qué servicios incluye."
        />
      ) : (
        <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {programs.map((p) => (
            <ProgramListRow
              key={p.id}
              program={p}
              onEdit={() => openEdit(p)}
              onArchiveToggle={() => void toggleArchive(p)}
              onDelete={() => void deleteProgram(p)}
            />
          ))}
        </ul>
      )}

      <ProgramFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        program={editing}
        onSaved={() => void reload()}
      />
    </DashboardPageShell>
  );
}
